import * as React from 'react'
import { z } from 'zod'
import { DndController } from '../../dnd-provider'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { Block, isReferenceBlock } from '@basehub/data/replicache/index'
import { useDndContext, useDroppable } from '@dnd-kit/core'
import { clsx } from 'clsx'
import { getId } from '@basehub/utils/id/index'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { toast } from 'sonner'
import { ReorderBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/reorder'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { invariant } from '@epic-web/invariant'
import { useBlockWrapperStore } from '../block-wrapper'
import { BlockVariant } from '..'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'

/* -------------------------------------------------------------------------------------------------
 * Drag and Drop Base
 * -----------------------------------------------------------------------------------------------*/

const dndType = 'editor-dnd'
const referenceDndType = 'editor-dnd:reference'

export const editorDndControllers = {
  dndType: dndType,
  onDragEnd: (event) => {
    if (!event.over) return
    const parsedDraggable = editorDndControllers.draggableDataSchema.safeParse(
      event.active.data.current
    )
    const parsedDroppable = editorDndControllers.droppableDataSchema.safeParse(
      event.over?.data.current
    )

    if (!parsedDraggable.success || !parsedDroppable.success) return

    const draggable = parsedDraggable.data
    const dropable = parsedDroppable.data

    const treeNode = document.querySelector(`#${dndTreeNodeId}`)

    if (treeNode) {
      const customEvent = new CustomEvent('dnd', {
        detail: {
          draggedItems: draggable,
          dropTarget: dropable,
        },
      })
      treeNode.dispatchEvent(customEvent)
    } else throw new Error('treeNode not found')
  },
  droppableDataSchema: z.object({
    newParentKey: z.string(),
    type: z.enum([dndType, referenceDndType]),
    intendedIndex: z.number(),
  }),
  draggableDataSchema: z.object({
    keysDragged: z.set(z.object({ label: z.string(), id: z.string() })),
    type: z.enum([dndType, referenceDndType]),
    index: z.number(),
    currentParentKey: z.string(),
  }),
} satisfies DndController

export const referenceDndControllers = {
  ...editorDndControllers,
  dndType: referenceDndType,
} satisfies DndController

export function isValidDnd({
  keysDragged,
  newParentKey,
  intendedIndex,
  dragItemIndex,
  allowDragToSamePosition,
  draggedType,
  allowedType,
  currentParentId,
}: {
  keysDragged: z.infer<
    typeof editorDndControllers.draggableDataSchema
  >['keysDragged']
  newParentKey: z.infer<
    typeof editorDndControllers.droppableDataSchema
  >['newParentKey']
  intendedIndex: number
  dragItemIndex: number
  allowDragToSamePosition: boolean
  draggedType: typeof referenceDndType | typeof dndType
  allowedType: typeof referenceDndType | typeof dndType
  currentParentId: string
}) {
  if (draggedType !== allowedType) return false

  const isDraggingThisBlock = Array.from(keysDragged).some(
    ({ id }) => id === newParentKey
  )
  if (isDraggingThisBlock) return false

  if (
    allowedType === 'editor-dnd:reference' &&
    newParentKey !== currentParentId
  ) {
    // ensure its dragging under the same reference block
    return false
  }

  if (allowDragToSamePosition) return true

  const isDraggingToSamePosition =
    // same index
    intendedIndex === dragItemIndex ||
    // also same index, cause this will be re-adjusted afterwards
    intendedIndex === dragItemIndex + 1

  return !isDraggingToSamePosition
}

export const dndTreeNodeId = 'editor-dnd-tree-node'

export function useEditorDndTreeNode({
  rootId,
  enabled,
  allowedType,
  currentParentId,
}: {
  rootId: string
  enabled: boolean
  allowedType: typeof referenceDndType | typeof dndType
  currentParentId: string
}) {
  const replicache = useRepoReplicache().data?.replicache
  const undoManager = useRepoUndoManager()
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getPath = useWorkingTreeStore((s) => s.tree?.getPath)

  // receive dnd events here
  React.useEffect(() => {
    if (!enabled) return
    const contentEl = document.querySelector(`#${dndTreeNodeId}`)
    if (!contentEl) return

    const handleDnd = async (event: unknown) => {
      if (!replicache) throw new Error('replicache not found')

      const dndResult = z
        .object({
          detail: z
            .object({
              draggedItems: editorDndControllers.draggableDataSchema,
              dropTarget: editorDndControllers.droppableDataSchema,
            })
            .or(
              z.object({
                draggedItems: referenceDndControllers.draggableDataSchema,
                dropTarget: referenceDndControllers.droppableDataSchema,
              })
            ),
        })
        .parse(event).detail

      if (
        isValidDnd({
          keysDragged: dndResult.draggedItems.keysDragged,
          newParentKey: dndResult.dropTarget.newParentKey,
          intendedIndex: dndResult.dropTarget.intendedIndex,
          dragItemIndex: dndResult.draggedItems.index,
          allowDragToSamePosition: true,
          allowedType: dndResult.dropTarget.type,
          currentParentId: dndResult.draggedItems.currentParentKey,
          draggedType: dndResult.draggedItems.type,
        })
      ) {
        if (dndResult.dropTarget.type === 'editor-dnd:reference') {
          // instead of reordering, we'll just update the reference targetBlockIds
          if (
            dndResult.draggedItems.keysDragged.size === 0 ||
            !getNode ||
            !getPath
          ) {
            return
          }
          invariant(
            dndResult.draggedItems.keysDragged.size === 1,
            'Only one block can be dragged at a time (for now at least)'
          )
          const _draggedId = dndResult.draggedItems.keysDragged.values().next()
            .value?.id
          invariant(
            typeof _draggedId === 'string',
            '_draggedId must be a string'
          )
          const draggedTargetNode = getNode(_draggedId)
          invariant(draggedTargetNode, 'draggedTargetNode not found')
          const draggedSourceBlockId = idToSourceBlockId(
            draggedTargetNode.value
          )
          const dropTargetNode = getNode(dndResult.dropTarget.newParentKey)
          invariant(dropTargetNode, 'dropTargetNode not found')
          invariant(
            isReferenceBlock(dropTargetNode.value),
            'dropTargetNode must be a reference block'
          )

          const dropTargetNewTargetBlockIds: string[] = []

          for (const [
            currentIndex,
            id,
          ] of dropTargetNode.value.value.targetBlockIds.entries()) {
            if (id === draggedSourceBlockId) continue
            if (currentIndex === dndResult.dropTarget.intendedIndex) {
              dropTargetNewTargetBlockIds.push(draggedSourceBlockId)
            }
            dropTargetNewTargetBlockIds.push(id)
          }
          if (!dropTargetNewTargetBlockIds.includes(draggedSourceBlockId)) {
            dropTargetNewTargetBlockIds.push(draggedSourceBlockId)
          }

          const params: UpdateBlockParams = {
            id: dndResult.dropTarget.newParentKey,
            instanceChildTarget: dropTargetNode.value.instanceChildTarget,
            data: {
              type: 'reference',
              value: {
                targetBlockIds: dropTargetNewTargetBlockIds,
              },
            },
            path: getPath(dropTargetNode, {
              required: true,
              format: 'string',
            }),
            isoNow: new Date().toISOString(),
            strategy: 'merge-deep',
          }
          const result = await replicache.mutate
            .updateBlock(params)
            .catch((err) => {
              console.error(err)
              if (err instanceof MutatorUserError) {
                toast.error(err.reason)
              } else {
                toast.error('An error occurred')
              }
            })
          if (result?.undo) {
            undoManager.current?.push({
              redo: [{ op: 'updateBlock', args: params }],
              undo: [
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
              ],
            })
          }
        } else {
          const params: ReorderBlockParams = {
            rootId,
            blockIds: Array.from(dndResult.draggedItems.keysDragged).map(
              ({ id }) => id
            ),
            targetBlockId: dndResult.dropTarget.newParentKey,
            intendedIndex: dndResult.dropTarget.intendedIndex,
            isoNow: new Date().toISOString(),
            idSeed: getId(),
          }
          const result = await replicache.mutate
            .reorderBlocks(params)
            .catch((err) => {
              console.error(err)
              if (err instanceof MutatorUserError) {
                toast.error(err.reason)
              } else {
                toast.error('An error occurred')
              }
            })
          if (result?.undo) {
            undoManager.current?.push({
              redo: [{ op: 'reorderBlocks', args: params }],
              undo: [
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
              ],
            })
          }
        }
      }
    }

    contentEl.addEventListener('dnd', handleDnd)

    return () => {
      contentEl.removeEventListener('dnd', handleDnd)
    }
  }, [
    replicache,
    rootId,
    enabled,
    undoManager,
    getNode,
    getPath,
    currentParentId,
    allowedType,
  ])
}

/* -------------------------------------------------------------------------------------------------
 * Drag and Drop Components
 * -----------------------------------------------------------------------------------------------*/

export const DroppableTopZone = ({
  block,
  parentId,
  index,
}: {
  block: Block
  parentId: string
  index: number
}) => {
  const intendedIndex = index
  const variant = useBlockWrapperStore((s) => s.variant)
  const allowedType =
    variant === 'nested-union' ? 'editor-dnd:reference' : 'editor-dnd'

  const { active } = useDndContext()
  const shouldDisableDroppableZone = React.useMemo(
    () =>
      block.type === 'list' &&
      String(active?.id ?? '').startsWith('list-entry'),
    [active?.id, block.type]
  )
  const droppable = useDroppable({
    id: `block-wrapper-droppable-top/${block.id}`,
    data: editorDndControllers.droppableDataSchema.parse({
      newParentKey: parentId,
      intendedIndex,
      type: allowedType,
    }),
    disabled: shouldDisableDroppableZone,
  })

  const isValid = React.useMemo(() => {
    if (!droppable.active || !droppable.isOver || !droppable.over) return false
    const parsed = editorDndControllers.draggableDataSchema.safeParse(
      droppable.active.data.current
    )

    if (!parsed.success) return

    const activeItemData = parsed.data

    return isValidDnd({
      keysDragged: activeItemData.keysDragged,
      newParentKey: parentId,
      intendedIndex,
      dragItemIndex: activeItemData.index,
      allowDragToSamePosition: false,
      currentParentId: parsed.data.currentParentKey,
      draggedType: parsed.data.type,
      allowedType,
    })
  }, [
    parentId,
    droppable.active,
    droppable.isOver,
    droppable.over,
    intendedIndex,
    allowedType,
  ])

  return (
    <div
      className={clsx(
        'pointer-events-none absolute left-0 top-0 h-1/2 w-full',
        !isValid && 'invisible'
      )}
      ref={droppable.setNodeRef}
    >
      <div className="absolute -top-px left-24 right-24 h-0.5 rounded-md bg-dark-control-base pointer-events-none" />
    </div>
  )
}

export const DroppableBottomZone = ({
  block,
  parentId,
  index,
}: {
  block: Block
  parentId: string
  index: number
}) => {
  const intendedIndex = index + 1
  const variant = useBlockWrapperStore((s) => s.variant)
  const allowedType =
    variant === 'nested-union' ? 'editor-dnd:reference' : 'editor-dnd'

  const { active } = useDndContext()
  const shouldDisableDroppableZone = React.useMemo(
    () =>
      block.type === 'list' &&
      String(active?.id ?? '').startsWith('list-entry'),
    [active?.id, block.type]
  )
  const droppable = useDroppable({
    id: `block-wrapper-droppable-bottom/${block.id}`,
    data: editorDndControllers.droppableDataSchema.parse({
      newParentKey: parentId,
      intendedIndex,
      type: allowedType,
    }),
    disabled: shouldDisableDroppableZone,
  })

  const isValid = React.useMemo(() => {
    if (!droppable.active || !droppable.isOver || !droppable.over) return false
    const parsed = editorDndControllers.draggableDataSchema.safeParse(
      droppable.active.data.current
    )

    if (!parsed.success) return

    const activeItemData = parsed.data

    return isValidDnd({
      keysDragged: activeItemData.keysDragged,
      newParentKey: parentId,
      intendedIndex,
      dragItemIndex: activeItemData.index,
      allowDragToSamePosition: false,
      currentParentId: parsed.data.currentParentKey,
      draggedType: parsed.data.type,
      allowedType,
    })
  }, [
    parentId,
    droppable.active,
    droppable.isOver,
    droppable.over,
    intendedIndex,
    allowedType,
  ])

  return (
    <div
      className={clsx(
        'pointer-events-none absolute left-0 bottom-0 h-1/2 w-full',
        !isValid && 'invisible'
      )}
      ref={droppable.setNodeRef}
    >
      <div className="absolute -bottom-px left-24 right-24 h-0.5 rounded-md bg-dark-control-base pointer-events-none" />
    </div>
  )
}

export const DroppableBottomFillZone = ({
  parentId,
  intendedIndex,
  variant,
}: {
  parentId: string
  intendedIndex: number
  variant: BlockVariant
}) => {
  const allowedType =
    variant === 'nested-union' ? 'editor-dnd:reference' : 'editor-dnd'

  const droppable = useDroppable({
    id: `block-droppable/${parentId}`,
    data: editorDndControllers.droppableDataSchema.parse({
      newParentKey: parentId,
      intendedIndex,
      type: allowedType,
    }),
  })

  const isValid = React.useMemo(() => {
    if (!droppable.active || !droppable.isOver || !droppable.over) return false
    const parsed = editorDndControllers.draggableDataSchema.safeParse(
      droppable.active.data.current
    )

    if (!parsed.success) return

    const activeItemData = parsed.data

    return isValidDnd({
      keysDragged: activeItemData.keysDragged,
      newParentKey: parentId,
      intendedIndex,
      dragItemIndex: activeItemData.index,
      allowDragToSamePosition: false,
      currentParentId: parsed.data.currentParentKey,
      draggedType: parsed.data.type,
      allowedType,
    })
  }, [
    parentId,
    droppable.active,
    droppable.isOver,
    droppable.over,
    intendedIndex,
    allowedType,
  ])

  return (
    <div
      className={clsx(
        'pointer-events-none absolute left-0 top-0 bottom-0 w-full',
        !isValid && 'invisible'
      )}
      ref={droppable.setNodeRef}
    >
      <div className="absolute -top-px left-24 right-24 mx-auto max-w-[calc(1280px-96px_*_2)] h-0.5 rounded-md bg-dark-control-base" />
    </div>
  )
}
